import {useMemo} from "react";
import {Area, Line, YAxis} from "recharts";
import {BaseChart, ChartComponent} from "./BaseChart";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {FieldResult} from "../../../types/domain/fieldResult";
import {useMeasureSystemUtils} from "../../../hooks/measureSystemUtilsHooks";
import {MeasureSystemUtils} from "../../../types/domain/measureSystemUtils";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";

export const AirTemperatureChart: ChartComponent = ({data}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const chartData = useMemo(() => data.map((r) => mapFieldResultsToChartData(r, msUtils)), [data, msUtils]);

    return (
        <BaseChart
            title={t("insights.charts.air_temperature.title")}
            yLabel={`${t("insights.charts.air_temperature.y_label")}, ${msUtils.getTemperatureUnits()}`}
            data={chartData}
            helpPopoverTranslationKey="help.popover.fields.air_temperature"
        >
            <Area
                type="monotone"
                dataKey="maxAirTemperature"
                name={t("insights.charts.air_temperature.area3_name")}
                unit={msUtils.getTemperatureUnits()}
                yAxisId="left"
                stroke="#EF6154"
                fill="#fcd3cd"
                strokeWidth={2}
                isAnimationActive={false}
                fillOpacity="1"
                opacity="1"
                baseValue="dataMin"
            />
            <Area
                type="monotone"
                dataKey="avgAirTemperature"
                name={t("insights.charts.air_temperature.area1_name")}
                unit={msUtils.getTemperatureUnits()}
                yAxisId="left"
                stroke="#00CA4D"
                fill="#A3FEC6"
                strokeWidth={2}
                isAnimationActive={false}
                fillOpacity="1"
                opacity="1"
                baseValue="dataMin"
            />
            <Area
                type="monotone"
                dataKey="minAirTemperature"
                name={t("insights.charts.air_temperature.area2_name")}
                unit={msUtils.getTemperatureUnits()}
                yAxisId="left"
                stroke="#3a48a2"
                fill="#5161C1"
                strokeWidth={2}
                isAnimationActive={false}
                fillOpacity="1"
                opacity="1"
                baseValue="dataMin"
            />
            <Line
                type="monotone"
                dataKey="cumulativeGdu"
                name={t("insights.charts.air_temperature.line_name")}
                yAxisId="right"
                stroke="#212963"
                fill="#212963"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
            <YAxis
                yAxisId="right"
                domain={["auto", "auto"]}
                fontSize={14}
                label={{
                    value: t("insights.charts.air_temperature.y_axis"),
                    angle: 90,
                    dx: 20,
                    dy: 0,
                    textAnchor: "middle",
                }}
                orientation="right"
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (r: FieldResult, msUtils: MeasureSystemUtils): DataProps => ({
    date: dayjs(r.date).toDate(),
    avgAirTemperature: msUtils.getTemperature(r.aggregatedProps.air_temperature_24),
    minAirTemperature: msUtils.getTemperature(r.aggregatedProps.air_temperature_min_24),
    maxAirTemperature: msUtils.getTemperature(r.aggregatedProps.air_temperature_max_24),
    cumulativeGdu: r.aggregatedProps.thermal_degrees_days_cumulative,
});

export interface DataProps extends TimeSeriesDataProps {
    avgAirTemperature: number;
    minAirTemperature: number;
    maxAirTemperature: number;
    cumulativeGdu: number;
}
